import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getOrderState, OrderStateContext } from "@insite/client-framework/Store/Data/Orders/OrdersSelectors";
import reorder from "@insite/client-framework/Store/Pages/OrderDetails/Handlers/Reorder";
import translate from "@insite/client-framework/Translate";
import { OrderModel } from "@insite/client-framework/Types/ApiModels";
import OrderDetailPageTypeLink from "@insite/content-library/Components/OrderDetailPageTypeLink";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import { HasToasterContext, withToaster } from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import React, { FC } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    order?: OrderModel;
    extendedStyles?: OrderTotalDisplayStyles;
}

const mapStateToProps = (state: ApplicationState) => {
    const order = getOrderState(state, state.pages.orderDetails.orderNumber).value;
    const settingsCollection = getSettingsCollection(state);
    return {
        order,
        enableVat: getSettingsCollection(state).productSettings.enableVat,
        showAddToCartConfirmationDialog: settingsCollection.productSettings.showAddToCartConfirmationDialog,
        isReordering: state.pages.orderDetails.isReordering,
        orderData: state.pages.orderStatus.order,
    };
};

const mapDispatchToProps = {
    reorder,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasToasterContext;

export interface OrderTotalDisplayStyles {
    container?: GridContainerProps;
    subtotalLabelGridItem?: GridItemProps;
    subtotalLabel?: TypographyProps;
    subtotalValueGridItem?: GridItemProps;
    subtotalValue?: TypographyProps;
    discountsLabelGridItem?: GridItemProps;
    discountsLabel?: TypographyProps;
    discountsValueGridItem?: GridItemProps;
    discountsValue?: TypographyProps;
    shippingAndHandlingLabelGridItem?: GridItemProps;
    shippingAndHandlingLabel?: TypographyProps;
    shippingAndHandlingValueGridItem?: GridItemProps;
    shippingAndHandlingValue?: TypographyProps;
    otherChargesLabelGridItem?: GridItemProps;
    otherChargesLabel?: TypographyProps;
    otherChargesValueGridItem?: GridItemProps;
    otherChargesValue?: TypographyProps;
    taxLabelGridItem?: GridItemProps;
    taxLabel?: TypographyProps;
    taxValueGridItem?: GridItemProps;
    taxValue?: TypographyProps;
    totalLabelGridItem?: GridItemProps;
    totalLabel?: TypographyProps;
    totalValueGridItem?: GridItemProps;
    totalValue?: TypographyProps;
    sideWrapper?: InjectableCss;
    reorderButton?: ButtonPresentationProps;
    spinner?: LoadingSpinnerProps;
}

export const orderTotalDisplayStyles: OrderTotalDisplayStyles = {
    container: {
        gap: 10,
        css: css`
            background-color: #fff;
            padding: 0;
        `,
    },
    subtotalLabelGridItem: { width: 6 },
    subtotalValueGridItem: {
        css: css`
            @media print {
                text-align: right;
            }
        `,
        width: 6,
    },
    subtotalValue: {
        css: css`
            margin-left: auto;
        `,
    },
    discountsLabelGridItem: { width: 6 },
    discountsValueGridItem: {
        css: css`
            @media print {
                text-align: right;
            }
        `,
        width: 6,
    },
    discountsValue: {
        css: css`
            margin-left: auto;
        `,
    },
    shippingAndHandlingLabelGridItem: { width: 6 },
    shippingAndHandlingValueGridItem: {
        css: css`
            @media print {
                text-align: right;
            }
        `,
        width: 6,
    },
    shippingAndHandlingValue: {
        css: css`
            margin-left: auto;
        `,
    },
    otherChargesLabelGridItem: { width: 6 },
    otherChargesValueGridItem: {
        css: css`
            @media print {
                text-align: right;
            }
        `,
        width: 6,
    },
    otherChargesValue: {
        css: css`
            margin-left: auto;
        `,
    },
    taxLabelGridItem: { width: 6 },
    taxValueGridItem: {
        css: css`
            @media print {
                text-align: right;
            }
        `,
        width: 6,
    },
    taxValue: {
        css: css`
            margin-left: auto;
        `,
    },
    totalLabelGridItem: { width: 6 },
    totalLabel: { weight: "bold" },
    totalValueGridItem: {
        css: css`
            @media print {
                text-align: right;
            }
        `,
        width: 6,
    },
    totalValue: {
        weight: "bold",
        css: css`
            margin-left: auto;
        `,
    },
    sideWrapper: {
        css: css`
            background-color: #fff;
            
            .border-seperator {
                border-bottom: 1px solid #C2D1D9;
                padding-top: 15px;
                margin:0 5px 15px 5px;
                width: 100%;
                display: block;
            }
            span.cart-items {
                background-color: #fff;
                border-bottom: 1px solid #C2D1D9;
                color: #333F48;
                font-family: 'Lato',sans-serif;
                font-size: 15px;
                font-weight: bold;
                letter-spacing: 0.17px;
                line-height: 18px;
                padding-bottom: 13px;
                margin-bottom: 15px;
                display: block;
            }
            > div {
                div:nth-child(odd) {
                    > span {
                        color: #535E64;
                        font-family: Roboto, sans-serif;
                        font-size: 16px;
                        font-weight: 500;
                        letter-spacing: -0.31px;
                        line-height: 23px;
                        &.total-value {
                            color: #C63527;
                            font-family: Roboto;
                            font-size: 22px;
                            font-weight: bold;
                            letter-spacing: -0.42px;
                            line-height: 23px;
                            text-align: right;
                        }
                    }
                    > span.subtotal-value {
                        font-weight: bold;
                        color: #535E64;
                    }
                    .total-label {
                        color: #333F48;
                        font-family: Roboto, sans-serif;
                        font-size: 20px;
                        font-weight: 500;
                        letter-spacing: -0.38px;
                        line-height: 23px;
                        text-align: left !important;
                    }
                }
                div:nth-child(even) {
                    text-align: right;
                    > span {
                        color: #333F48;
                        font-family: Roboto;
                        font-size: 20px;
                        letter-spacing: -0.38px;
                        line-height: 23px;
                    }
                    > span.subtotal-value {
                        font-weight: bold;
                    }
                }
            }
        `,
    },
    reorderButton: {
        buttonType: "solid",
        variant: "primary",
        css: css`
            color: #333F48;
            font-family: 'Roboto', sans-serif;
            font-size: 14px;
            font-weight: bold;
            text-align: center;
            text-transform: uppercase;
            width: 100%;
            margin: 35px 0 15px;
        `,
    },
    spinner: {
        size: 22,
        css: css`
            margin: 8px 50px 0 34px;
        `,
    },
};

const OrderTotalDisplay: FC<Props> = ({ order, toaster, reorder, enableVat, extendedStyles, 
    showAddToCartConfirmationDialog, isReordering,orderData }) => {
    const [styles] = React.useState(() => mergeToNew(orderTotalDisplayStyles, extendedStyles));
    if(!order && orderData)
    {
        order=orderData;
    }
    if (!order) {
        return null;
    }

    return (
        <>
        <Typography as="div" {...styles.sideWrapper}  className="side-wrapper">
            { order?.orderLines?.length === 1 ?
            <Typography className="cart-items"> {`${translate("There is") } ${order?.orderLines?.length } ${translate("item in this order")}`}</Typography> :
            <Typography className="cart-items"> {`${translate("There are") } ${order?.orderLines?.length } ${translate("items in this order")}`}</Typography> 
            }
            <GridContainer {...styles.container}>
                <GridItem {...styles.subtotalLabelGridItem}>
                    <Typography {...styles.subtotalLabel}>
                        {translate("Subtotal") + (enableVat ? ` (${translate("Ex. VAT")})` : "")}
                    </Typography>
                </GridItem>
                <GridItem {...styles.subtotalValueGridItem}>
                    <Typography {...styles.subtotalValue} className="subtotal-value">{order.orderSubTotalDisplay}</Typography>
                </GridItem>
                {order?.discountAmount > 0 && (
                    <>
                        <GridItem {...styles.discountsLabelGridItem}>
                            <Typography {...styles.discountsLabel}>{translate("Discounts")}</Typography>
                        </GridItem>
                        <GridItem {...styles.discountsValueGridItem}>
                            <Typography {...styles.discountsValue}>{`-${order?.orderDiscountAmountDisplay}`}</Typography>
                        </GridItem>
                    </>
                )}
                {order.shippingCharges + order.handlingCharges > 0 && (
                    <>
                        <GridItem {...styles.shippingAndHandlingLabelGridItem}>                       
                        <Typography {...styles.shippingAndHandlingLabel}>{translate("Shipping & Handling")}</Typography>
                    </GridItem>
                    <GridItem {...styles.shippingAndHandlingValueGridItem}>
                            <Typography {...styles.shippingAndHandlingValue}>{order.shippingAndHandlingDisplay}</Typography>
                    </GridItem>
                    
                </>
                )}
                {order.otherCharges > 0 && (
                    <>
                        <GridItem {...styles.otherChargesLabelGridItem}>
                            <Typography {...styles.otherChargesLabel}>{translate("Other Charges")}</Typography>
                        </GridItem>
                        <GridItem {...styles.otherChargesValueGridItem}>
                            <Typography {...styles.otherChargesValue}>{order.otherChargesDisplay}</Typography>
                        </GridItem>
                    </>
                )}
                {(!order.orderHistoryTaxes || order.orderHistoryTaxes.length === 0) && (
                    <>
                        <GridItem {...styles.taxLabelGridItem}>
                            <Typography {...styles.taxLabel}>{enableVat ? translate("VAT") : translate("Tax")}</Typography>
                        </GridItem>
                        <GridItem {...styles.taxValueGridItem}>
                            <Typography {...styles.taxValue} data-test-selector="orderDetails_totalTaxDisplay">
                                {order.totalTaxDisplay}
                            </Typography>
                        </GridItem>
                    </>
                )}
                {order.orderHistoryTaxes &&
                order.orderHistoryTaxes.map(tax => (
                    <>
                        <GridItem {...styles.taxLabelGridItem}>
                            <Typography>{tax.taxDescription || translate("Tax")}</Typography>
                        </GridItem>
                        <GridItem {...styles.taxValueGridItem}>
                            <Typography {...styles.taxValue}>{tax.taxAmountDisplay}</Typography>
                        </GridItem>
                    </>
                ))}
                <Typography as="span" className="border-seperator"></Typography>    
                <GridItem {...styles.totalLabelGridItem}>
                    <Typography {...styles.totalLabel} className="total-label">{translate("Total")}</Typography>
                </GridItem>
                <GridItem {...styles.totalValueGridItem}>
                    <Typography {...styles.totalValue} className="total-value" data-test-selector="orderDetail_orderGrandTotalDisplay">
                        {order.orderGrandTotalDisplay}
                    </Typography>
                </GridItem>
            </GridContainer>
            {/* {!isReordering && (
            <Button
                {...styles.reorderButton}
                onClick={onClickReorder}
                disabled={isReordering}
                >   
                    <span>{translate("Reorder")}</span>
                </Button>
            )}
            {isReordering && <LoadingSpinner {...styles.spinner} />} */}
        </Typography>        
        
        </>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(OrderTotalDisplay);
